import { Component, ElementRef, OnInit, ViewChildren, ChangeDetectorRef } from "@angular/core";
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import firebase from "firebase";
import { Account } from "src/models/account.model";
import Swal from "sweetalert2";
import * as moment from "moment";
import { UserLogs } from "../../../models/user.model";
import { RefresherService } from "../../../services/refresher.service";
import { AnalyticsService } from "src/services/analytics.service";
import { FormBuilder, FormGroup } from "@angular/forms";

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.css']

})
export class UserComponent implements OnInit {

  @ViewChildren("activeSessionElement") private activeSessionElement: ElementRef;

  date = new Date();
  selectedDate: string;
  isCurrentRouteDashboard: boolean;

  // Date range properties
  startDate: Date;
  endDate: Date;
  activeQuickDateRange: string = '';
  private isSettingQuickRange: boolean = false;
  private isAutoAdjustingDates: boolean = false;
  private isUpdatingAnalytics: boolean = false;
  private isLoadingData: boolean = false;
  minDate: Date;
  maxDate: Date;

  // Form for date inputs
  dateForm: FormGroup;

  // Sidebar properties
  sidebarOpen: boolean = false;

  user: UserLogs = <UserLogs>{};
  account: Account;
  isAdmin: boolean = false;
  public activeSession: number = 0;
  public activeEntrySession: number = 0;
  public activeExitSession: number = 0;

  public logs = [];

  //Data object for listing items
  tableData: any[] = [];
  zones: any[] = [];
  selectedZone: any;
  currentZoneId: string;
  newZoneId: string;

  constructor(
    private router: Router,
    private refresher: RefresherService,
    private analyticsService: AnalyticsService,
    private cdr: ChangeDetectorRef,
    private formBuilder: FormBuilder
  ) {
    this.account = JSON.parse(localStorage.getItem('account'));
    this.zones = this.account?.settings?.zones || [];
    this.user = JSON.parse(localStorage.getItem('user'));

    // Initialize currentUser if it doesn't exist but user is logged in
    this.initializeCurrentUser();

    // Check currentUser localStorage (this is the main user object)
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));

    // Check if user is admin (admins have no 31-day restriction)
    const userRoles = (this.user as any)?.roles || (currentUser as any)?.roles;
    const isAdminByRole = userRoles?.admin === true || userRoles?.superAdmin === true;

    // Check direct role property (currentUser.role === "admin")
    const isAdminByRoleString = (currentUser as any)?.role === 'admin' || (currentUser as any)?.role === 'superadmin';

    const isAdminByFlag =
      (this.user as any)?.isAdmin === true ||
      (currentUser as any)?.isAdmin === true ||
      (this.account as any)?.isAdmin === true;
    const isAdminByUrl = window.location.href.indexOf('/admin/') !== -1;

    // Admin if any of these conditions are true
    this.isAdmin = isAdminByRole || isAdminByRoleString || isAdminByFlag || isAdminByUrl;

    // Initialize date range with today
    const today = new Date();
    this.startDate = new Date(today);
    this.endDate = new Date(today);
    this.selectedDate = today.toISOString().split('T')[0];

    // Initialize form with date controls
    this.dateForm = this.formBuilder.group({
      startDate: [today],
      endDate: [today]
    });

    // Set min and max dates
    this.minDate = new Date(today.getFullYear() - 1, 0, 1); // 1 year ago
    this.maxDate = new Date(today); // Today

    // Set default active quick range
    this.activeQuickDateRange = 'today';

    // Subscribe to form control changes
    this.dateForm.get('startDate')?.valueChanges.subscribe(value => {
      if (value && !this.isAutoAdjustingDates && !this.isUpdatingAnalytics && !this.isLoadingData) {
        this.startDate = new Date(value);
        if (!this.isSettingQuickRange) {
          this.activeQuickDateRange = '';
          this.autoAdjustDateRange('start');
        }
      }
    });

    this.dateForm.get('endDate')?.valueChanges.subscribe(value => {
      if (value && !this.isAutoAdjustingDates && !this.isUpdatingAnalytics && !this.isLoadingData) {
        this.endDate = new Date(value);
        if (!this.isSettingQuickRange) {
          this.activeQuickDateRange = '';
          this.autoAdjustDateRange('end');
        }
      }
    });

    // Listen for route changes
    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd)
    ).subscribe((res) => {
      const urlParts = res.url.split('/').filter(part => part !== ''); // Remove empty parts
      const isReportsPage = res.url.indexOf('/reports') !== -1;
      const isEmployeesPage = res.url.indexOf('/employees') !== -1;
      const isSettingsPage = res.url.indexOf('/settings') !== -1;
      const isZonesPage = res.url.indexOf('/zones') !== -1;
      const isProfilePage = res.url.indexOf('/profile') !== -1;
      const isLogsPage = res.url.indexOf('/logs') !== -1;

      // If the page is reports, employees, settings, zones, profile, or logs
      // just set dashboard flag to false and do not redirect
      if (isReportsPage || isEmployeesPage || isSettingsPage ||
        isZonesPage || isProfilePage || isLogsPage) {
        this.isCurrentRouteDashboard = false;
        return;
      }

      // Check if URL is for dashboard (with or without trailing slash)
      if (res.url.match(/\/user\/dashboard\/?$/) ||
        res.url.match(/\/user\/dashboard\/\s*$/)) {
        // Exact match for /user/dashboard/ or /user/dashboard with no zone ID
        this.isCurrentRouteDashboard = true;

        if (this.zones && this.zones.length > 0) {
          this.currentZoneId = this.zones[0].zone_id;
          this.selectedZone = this.zones[0];

          // Navigate to dashboard with the default zone
          setTimeout(() => {
            this.router.navigate([this.account.account_key + '/user/dashboard/' + this.currentZoneId],
              { replaceUrl: true });
          }, 100);
        }
        return;
      }

      // Check if the URL contains accounts or dashboard with zone ID
      if (res.url.indexOf('/admin/accounts') !== -1 ||
        res.url.indexOf('/user/dashboard/') !== -1) {
        // Any accounts page or dashboard with zone ID, set dashboard flag true
        this.isCurrentRouteDashboard = true;

        // Extract zone ID from URL if it's a dashboard URL
        if (res.url.indexOf('/user/dashboard/') !== -1) {
          const lastPart = urlParts[urlParts.length - 1];
          // Only set zone ID if not an empty string
          if (lastPart && lastPart !== 'dashboard') {
            this.currentZoneId = lastPart;
          }
        }
      } else {
        this.isCurrentRouteDashboard = false;
      }
    });

    this.refresher.notifyObservable$.subscribe(async (res) => {
      if (res.loadDashboard) {
        await this.loadData();
      }
      // Set isCurrentRouteDashboard flag based on notification
      if (res.isDashboard !== undefined) {
        this.isCurrentRouteDashboard = res.isDashboard;
      }
    });
  }

  /**
   * Initialize currentUser in localStorage if it doesn't exist
   * This ensures currentUser is available even when user refreshes the page
   */
  private initializeCurrentUser(): void {
    const currentUser = localStorage.getItem('currentUser');
    const user = this.user as any; // Firebase Auth user object
    const account = this.account;

    // If currentUser already exists, no need to re-initialize
    if (currentUser && currentUser !== 'null') {
      return;
    }

    // If user is logged in but currentUser doesn't exist, fetch user data from Firestore
    if (user && user.uid) {
      firebase.firestore().collection('users').doc(user.uid).get().then((doc) => {
        if (doc.exists) {
          const userData = doc.data();
          const userRole = userData.roles?.superAdmin ? 'superadmin' :
                          userData.roles?.admin ? 'admin' :
                          userData.roles?.editor ? 'editor' : 'user';

          // Create and store currentUser object
          localStorage.setItem('currentUser', JSON.stringify({
            id: user.uid,
            username: userData.fullName || user.displayName || user.email,
            fullname: userData.fullName || user.displayName,
            role: userRole,
            roles: userData.roles,
            email: user.email,
            phoneNumber: user.phoneNumber,
            account_key: account?.account_key,
            token: null
          }));

          // Update isAdmin flag if needed
          if (userRole === 'admin' || userRole === 'superadmin') {
            this.isAdmin = true;
          }
        }
      });
    }
  }

  async ngOnInit() {
    await this.loadData();
    await this.getAccountByKey(this.account.account_key);

    // Make sure analytics service is updated with the current date
    this.updateAnalyticsDateRange(this.account.account_key);

    // Expose method to manually enable admin mode via console
    (window as any).enableAdminMode = () => {
      this.isAdmin = true;
    };
  }

  async ngAfterViewInit() {

  }

  onDateChange() {
    // Delegate to main date range change handler
    this.onDateRangeChange();
  }

  /**
   * Auto-adjust date range to ensure it doesn't exceed 31 days
   *
   * This method prevents hanging/looping issues when users select date ranges
   * exceeding 31 days by automatically adjusting the opposite date to maintain
   * a valid 31-day maximum range. Uses intelligent logic to determine whether
   * to adjust start or end date based on user interaction.
   *
   * Note: Admin users are exempt from the 31-day restriction.
   *
   * @param changedField - Indicates which date field was changed ('start' or 'end')
   */
  private autoAdjustDateRange(changedField: 'start' | 'end') {
    if (!this.startDate || !this.endDate || this.isAutoAdjustingDates || this.isUpdatingAnalytics || this.isLoadingData) return;

    // Admin users can select any date range without restriction
    if (this.isAdmin) {
      setTimeout(() => {
        this.onDateRangeChange();
      }, 50);
      return;
    }

    const startDate = new Date(this.startDate);
    const endDate = new Date(this.endDate);

    // Calculate the difference in days
    const diffDays = this.calculateDaysDifference(startDate, endDate);

    // Edge case: When exactly 1 month is selected (31/32 days), it can cause looping
    const isExactMonth = this.isExactlyOneMonth(startDate, endDate);

    // If range is within 31 days, no adjustment needed - trigger update
    if (diffDays <= 31) {
      // Use a micro-delay to ensure form controls are settled
      // Only trigger date change if not exactly one month (prevents looping)
      if (!isExactMonth || changedField === 'start') {
        setTimeout(() => {
          this.onDateRangeChange();
        }, 50);
      }
      return;
    }

    this.isAutoAdjustingDates = true;
    let adjustedStartDate: Date;
    let adjustedEndDate: Date;
    let showNotification = false;

    if (changedField === 'end') {
      // User changed end date, adjust start date to maintain 31-day range
      adjustedEndDate = new Date(endDate);
      adjustedStartDate = new Date(endDate);
      adjustedStartDate.setDate(endDate.getDate() - 31);

      // Ensure start date doesn't go below minimum date
      if (adjustedStartDate < this.minDate) {
        adjustedStartDate = new Date(this.minDate);
        adjustedEndDate = new Date(this.minDate);
        adjustedEndDate.setDate(this.minDate.getDate() + 31);

        // Ensure end date doesn't exceed maximum date
        if (adjustedEndDate > this.maxDate) {
          adjustedEndDate = new Date(this.maxDate);
        }
      }
      showNotification = true;
    } else {
      // User changed start date, adjust end date to maintain 31-day range
      adjustedStartDate = new Date(startDate);
      adjustedEndDate = new Date(startDate);
      adjustedEndDate.setDate(startDate.getDate() + 31);

      // Ensure end date doesn't exceed maximum date
      if (adjustedEndDate > this.maxDate) {
        adjustedEndDate = new Date(this.maxDate);
        adjustedStartDate = new Date(this.maxDate);
        adjustedStartDate.setDate(this.maxDate.getDate() - 31);

        // Ensure start date doesn't go below minimum date
        if (adjustedStartDate < this.minDate) {
          adjustedStartDate = new Date(this.minDate);
        }
      }
      showNotification = true;
    }

    // Update the dates
    this.startDate = adjustedStartDate;
    this.endDate = adjustedEndDate;

    // Update form controls without triggering change events
    this.dateForm.patchValue({
      startDate: adjustedStartDate,
      endDate: adjustedEndDate
    }, { emitEvent: false });

    setTimeout(() => {
      this.isAutoAdjustingDates = false;
      if (showNotification) {
        this.showDateAdjustmentNotification(diffDays);
      }
      // Delay analytics update to prevent immediate cascading events
      setTimeout(() => {
        this.onDateRangeChange();
      }, 200);
    }, 100);
  }

  /**
   * Show notification when dates are auto-adjusted
   */
  private showDateAdjustmentNotification(originalDays: number) {
    const formatDate = (date: Date) => moment(date).format('MMM DD, YYYY');

    Swal.fire({
      title: 'Date Range Adjusted',
      html: `The selected range was <strong>${originalDays} days</strong>, which exceeds the maximum limit.<br><br>
             <strong>Automatically adjusted to:</strong><br>
             📅 Start: ${formatDate(this.startDate)}<br>
             📅 End: ${formatDate(this.endDate)}<br><br>
             <em>Maximum range allowed is 31 days.</em>`,
      icon: 'info',
      confirmButtonText: 'Got it',
      timer: 5000,
      timerProgressBar: true,
      toast: true,
      position: 'top-end',
      showConfirmButton: false
    });
  }

  /**
   * Handle date range changes
   */
  onDateRangeChange() {
    if (!this.validateDateRangeInputs() || this.isUpdatingAnalytics || this.isLoadingData) return;

    this.isUpdatingAnalytics = true;
    this.updateAnalyticsDateRange(this.account.account_key);
    this.loadData();

    // Reset flag after a delay to allow analytics service to process (longer delay to match analytics debounce)
    setTimeout(() => {
      this.isUpdatingAnalytics = false;
      // Additional check if it's a one month selection
      if (this.isExactlyOneMonth(new Date(this.startDate), new Date(this.endDate))) {
        // One final dateRangeChanged event for 1-month selections
        const startTimestamp = this.getStartDate();
        const endTimestamp = this.getEndDate();
        this.analyticsService.finalizeMonthRange(startTimestamp, endTimestamp, this.account.account_key);
      }
    }, 800);
  }

  /**
   * Handle manual date input changes
   */
  onDateInputChange() {
    if (!this.isSettingQuickRange) {
      this.activeQuickDateRange = '';
      this.cdr.detectChanges();
    }
  }

  /**
   * Set quick date range shortcuts
   */
  setQuickDateRange(range: string) {
    this.isSettingQuickRange = true;
    this.activeQuickDateRange = range;

    const today = moment();
    let startDate: moment.Moment;
    let endDate: moment.Moment;

    switch (range) {
      case 'today':
        startDate = today.clone();
        endDate = today.clone();
        break;
      case 'yesterday':
        startDate = today.clone().subtract(1, 'day');
        endDate = today.clone().subtract(1, 'day');
        break;
      case 'thisWeek':
        startDate = today.clone().startOf('week');
        endDate = today.clone().endOf('week');
        break;
      case 'lastWeek':
        startDate = today.clone().subtract(1, 'week').startOf('week');
        endDate = today.clone().subtract(1, 'week').endOf('week');
        break;
      case 'thisMonth':
        startDate = today.clone().startOf('month');
        endDate = today.clone().endOf('month');
        break;
      default:
        startDate = today.clone();
        endDate = today.clone();
        break;
    }

    // Update form controls
    this.dateForm.patchValue({
      startDate: startDate.toDate(),
      endDate: endDate.toDate()
    });

    // Update component properties
    this.startDate = startDate.toDate();
    this.endDate = endDate.toDate();

    setTimeout(() => {
      this.isSettingQuickRange = false;
      this.onDateRangeChange();
    }, 100);
  }

  /**
   * Validate date range inputs (simplified for auto-adjustment system)
   */
  private validateDateRangeInputs(): boolean {
    if (!this.startDate || !this.endDate) {
      // Silent return if dates are not yet set
      return false;
    }

    // Ensure dates are valid Date objects
    const startDate = new Date(this.startDate);
    const endDate = new Date(this.endDate);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      Swal.fire({
        title: 'Invalid Date Format',
        text: 'Please select valid dates using the calendar picker.',
        icon: 'warning',
        confirmButtonText: 'OK',
        toast: true,
        position: 'top-end',
        timer: 3000,
        showConfirmButton: false
      });
      return false;
    }

    // Check if start date is after end date (auto-adjustment handles range limits)
    if (startDate > endDate) {
      Swal.fire({
        title: 'Invalid Date Range',
        text: 'Start date cannot be later than end date.',
        icon: 'warning',
        confirmButtonText: 'OK',
        toast: true,
        position: 'top-end',
        timer: 3000,
        showConfirmButton: false
      });
      return false;
    }

    // Note: Date range limit validation is now handled by auto-adjustment
    return true;
  }

  /**
   * Calculate days difference between two dates (utility method)
   */
  private calculateDaysDifference(startDate: Date, endDate: Date): number {
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  /**
   * Checks if a date range is exactly 31 days (or 1 month)
   * Includes both start and end date in the count
   */
  private isExactlyOneMonth(startDate: Date, endDate: Date): boolean {
    // Calculate inclusive days (adding 1 because both start and end dates are counted)
    const diffDays = this.calculateDaysDifference(startDate, endDate) + 1;
    return diffDays === 31 || diffDays === 32;  // Allow 31 or 32 days to account for different month lengths
  }

  private updateAnalyticsDateRange(account_key: string) {
    const startTimestamp = this.getStartDate();
    const endTimestamp = this.getEndDate();
    this.analyticsService.setDateRange(startTimestamp, endTimestamp, account_key);
  }

  async loadData() {
    // Prevent concurrent data loading
    if (this.isLoadingData) {
      return;
    }

    if (!this.zones || this.zones.length === 0) {
      return;
    }

    this.isLoadingData = true;

    // Don't redirect if we're not on the dashboard
    if (!this.isCurrentRouteDashboard) {
      // Just set a default zone for data purposes
      this.selectedZone = this.zones[0];
      this.currentZoneId = this.selectedZone.zone_id;
      this.isLoadingData = false;
      return;
    }

    if (this.currentZoneId) {
      if (this.currentZoneId === 'reports') {
        this.selectedZone = this.zones[0];
      } else {
        this.selectedZone = this.zones.find(z => z.zone_id == this.currentZoneId);
        if (!this.selectedZone) {
          // If zone not found (e.g., invalid ID), use first zone
          this.selectedZone = this.zones[0];
          this.currentZoneId = this.selectedZone.zone_id;
        }
      }
    } else {
      this.selectedZone = this.zones[0];
      this.currentZoneId = this.selectedZone.zone_id;
    }

    if (this.newZoneId == undefined) {
      this.newZoneId = this.selectedZone?.zone_id;
    }

    if (this.newZoneId != this.currentZoneId) {
      if (this.newZoneId != undefined && this.isCurrentRouteDashboard) {
        this.router.navigateByUrl(this.account.account_key + '/user/dashboard/' + this.newZoneId);
      }

      await this.getCountLogsActiveSessionCounter();
      await this.getCountLogsEntry();
      await this.getCountLogsExit();
    } else {
      if (this.newZoneId && this.isCurrentRouteDashboard) {
        this.router.navigateByUrl(this.account.account_key + '/user/dashboard/' + this.newZoneId);
      }

      await this.getCountLogsActiveSessionCounter();
      await this.getCountLogsEntry();
      await this.getCountLogsExit();
    }

    // Update Analytics Service zone only (date range updated separately)
    this.analyticsService.setZoneId(this.selectedZone?.zone_id);

    // Reset loading flag
    this.isLoadingData = false;
  }

  async getAccountByKey(account_key: string) {
    const query = firebase.firestore().collection('accounts');
    query
      .where("account_key", "==", account_key)
      .limit(1).onSnapshot((snapshots) => {

        if (snapshots.empty) return;

        snapshots.forEach((doc) => {
          let account = doc.data();
          account.account_id = doc.id;
          localStorage.removeItem('account');
          localStorage.setItem('account', JSON.stringify(account));

          // Update zones if needed
          this.account = account;
          if (account.settings?.zones) {
            this.zones = account.settings.zones;
            // Reload if zones have changed
            if (!this.selectedZone) {
              this.loadData();
            }
          }
        });
      });
  }

  async onSelectZone(value: any) {
    this.newZoneId = value;
    // Zone change doesn't need analytics date range update, just load data
    this.loadData();
  }

  getStartDate() {
    if (!this.startDate) return this.getStartOfToday();

    const startDate = new Date(this.startDate);
    startDate.setHours(0, 0, 0, 0);
    return firebase.firestore.Timestamp.fromDate(startDate);
  }

  getEndDate() {
    if (!this.endDate) return firebase.firestore.Timestamp.fromDate(new Date());

    const endDate = new Date(this.endDate);
    endDate.setHours(23, 59, 59, 999); // End of the selected day
    return firebase.firestore.Timestamp.fromDate(endDate);
  }

  getStartOfToday() {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return firebase.firestore.Timestamp.fromDate(now);
  }

  async getCountLogsActiveSessionCounter() {
    if (this.selectedZone === undefined) return;

    const query = firebase.firestore().collection('logs');
    const observer = query
      .where('account_key', '==', this.account.account_key)
      .where('zone_id', '==', this.selectedZone.zone_id)
      .where('status', '==', 'incomplete')
      .where('time_out', '==', null)
      .where('timestamp', '>=', this.getStartDate())
      .where('timestamp', '<=', this.getEndDate())
      .orderBy('timestamp', 'asc')
      .onSnapshot(async (documentSnapshots) => {
        this.activeSession = documentSnapshots.size;
      });
  }

  async getCountLogsEntry() {
    if (this.selectedZone === undefined) return;

    const query = firebase.firestore().collection('logs');
    const observer = query
      .where('account_key', '==', this.account.account_key)
      .where('zone_id', '==', this.selectedZone.zone_id)
      .where('timestamp', '>=', this.getStartDate())
      .where('timestamp', '<=', this.getEndDate())
      .onSnapshot(querySnapshot => {
        this.activeEntrySession = querySnapshot.size;
      });
  }

  async getCountLogsExit() {
    if (this.selectedZone === undefined) return;

    const query = firebase.firestore().collection('logs');
    const observer = query
      .where('account_key', '==', this.account.account_key)
      .where('zone_id', '==', this.selectedZone.zone_id)
      .where('status', '==', 'completed')
      .where('timestamp', '>=', this.getStartDate())
      .where('timestamp', '<=', this.getEndDate())
      .onSnapshot(querySnapshot => {
        this.activeExitSession = querySnapshot.size;
      });
  }

  async viewActiveSessionUsers() {
    await Swal.fire({
      title: '<strong>Active session</strong>',
      html: '<div class="swal-container">' +
        '<div class="block w-full overflow-x-auto">' +
        '<table class="items-center w-full bg-transparent border-collapse" style="background: #eeeeee;border-radius: 6px;color: #000000;">' +
        '<thead>' +
        '<th class="px-6 align-middle border border-solid py-3 text-sm  border-l-0 border-r-0 whitespace-nowrap font-semibold text-center">Plate Number</th>' +
        '<th class="px-6 align-middle border border-solid py-3 text-sm  border-l-0 border-r-0 whitespace-nowrap font-semibold text-center">Entry Time</th>' +
        '</thead>' +
        '<tbody style="background: #ffffff;" class="swal-inner-container">' +
        '</tbody>' +
        '</table>' +
        '</div>' +
        '</div>' +
        '<div class="relative flex flex-col min-w-0 break-words xl:w-12/12 mb-6 mt-10">' +
        '<div class="text-center action-btn">' +
        '<button class="bg-red-400 text-white active:bg-red-600 font-bold uppercase text-xs px-4 py-2 rounded\n' +
        '              shadow hover:shadow-md outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150' +
        '              text-sm loadMore">Load more</button>' +
        '</div>' +
        '</div>',
      showCloseButton: true,
      showConfirmButton: false,
      didOpen(popup: HTMLElement) {

        const container = document.querySelector('.swal-inner-container');

        let account = JSON.parse(localStorage.getItem('account'));

        //Store last document
        let latestDoc = null;

        const getCountLogsActiveSession = async () => {

          const query = firebase.firestore().collection('logs');
          const observer = query
            .where('account_key', '==', account.account_key)
            .where('zone_id', '==', this.selectedZone.zone_id.zone_id)
            .where('status', '==', 'incomplete')
            .where('time_out', '==', null)
            .where('timestamp', '>=', this.getStartDate())
            .where('timestamp', '<=', this.getEndDate())
            .orderBy('timestamp', 'asc')
            .startAfter(latestDoc || 0)
            .limit(10)
            .get()
            .then(async (documentSnapshots) => {

              if (!documentSnapshots.docs.length) {
                return false;
              }

              latestDoc = documentSnapshots.docs[documentSnapshots.docs.length - 1];

              let tableData = [];
              for (let item of documentSnapshots.docs) {
                const data = item.data();
                data.id = item.id;

                if (!data.plate_number) {
                  data.plate_number = data.code;
                }

                data.user_vehicleId = await getUserLogVehicle(data.code);

                tableData.push(data);
              }

              Promise.all(tableData).then((response) => {
                let logs = response.sort((a, b) => a.timestamp > b.timestamp ? 1 : -1);
                let template = '';
                logs.forEach((item) => {
                  template += '<tr>' +
                    '<td class="border-t-0  align-middle border-l-0 border-r-0 text-sm whitespace-nowrap p-2 text-center" >'
                    + '<a href="' + ['/' + item.account_key + '/user/vehicles/vehicles-edit/' + item.user_vehicleId] + '" class="text-red-500" target="_blank">'
                    + item.plate_number
                    + '</a>' +
                    '</td>' +
                    '<td class="border-t-0  align-middle border-l-0 border-r-0 text-sm whitespace-nowrap p-2 text-center">' + moment(item.time_in.seconds * 1000).format('hh:mm A') + '</td>' +
                    '</tr>'
                });

                container.innerHTML += template;

              }).catch((e) => {
                //Handle errors
              });

            });

        }

        const getUserLogVehicle = async (code) => {
          return new Promise(async (resolve) => {
            const userRef = firebase.firestore().collection('users_vehicle')
            const query = await userRef
              .where('account_key', '==', account.account_key)
              .where("code", "==", code)
              .limit(1)
              .get()
              .then((documentSnapshots: any) => {

                if (!documentSnapshots.docs.length) {
                  return false;
                }

                let vehicle_id;
                for (let item of documentSnapshots.docs) {
                  if (item.data()) {
                    vehicle_id = item.data().id;
                  } else {
                    vehicle_id = null;
                  }
                }

                resolve(vehicle_id);

              });

          });
        }

        getCountLogsActiveSession();

        const loadMore = document.querySelector('.action-btn button.loadMore');
        const handleClick = () => { getCountLogsActiveSession() }
        loadMore.addEventListener('click', handleClick);
      }
    });

  }

  async runExitMatching() {

    Swal.fire({
      title: 'Are you sure?',
      text: "Run checking for exit. This will take longer.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, proceed!',
      cancelButtonText: 'No, cancel!',
      reverseButtons: true
    }).then(async (result) => {
      if (result.isConfirmed) {

        await Swal.fire({
          title: 'Run checking for exit. Please wait.',
          html: '<div>' +
            '<div class="inner-logs">' +
            '<span></span>/<b></b>' +
            '<p style="color: #13ce66;" class="done"></p>' +
            '</div>' +
            '</div>',
          //timer: 2000,
          timerProgressBar: true,
          allowOutsideClick: false,
          // showCloseButton: true,
          // showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();

            let account = JSON.parse(localStorage.getItem('account'));
            const container = Swal.getHtmlContainer().querySelector('.inner-logs');
            const span = Swal.getHtmlContainer().querySelector('span');
            const b = Swal.getHtmlContainer().querySelector('b');
            const done = Swal.getHtmlContainer().querySelector('.done');

            const subscribeExit = async () => {
              const query = firebase.firestore().collection('exit_logs');
              const observer = await query
                .where('account_key', '==', account.account_key)
                .where('updatedAt', '>=', this.getStartDate())
                .where('updatedAt', '<=', this.getEndDate())
                .where('matched', '==', false)
                .orderBy('updatedAt', 'desc')
                .get()
                .then(querySnapshot => {

                  if (querySnapshot.empty) {
                    Swal.fire(
                      'Oops! No data found.',
                      'Please come back later.',
                      'error'
                    )
                    return;
                  }

                  let array = [];
                  querySnapshot.docChanges().forEach((change) => {
                    if (change.type === 'added') {
                      array.push(change.doc.data());
                    }
                  });

                  Promise.all(array).then((res) => {
                    span.textContent = '0';
                    b.textContent = array.length.toString();
                    processArray(res).then(() => {
                      done.textContent = 'ALL DONE!';
                      Swal.hideLoading();
                    });

                  });

                });
            }

            // utility function that returns a promise that resolves after t milliseconds
            const delay = async (t) => {
              return new Promise(resolve => {
                setTimeout(resolve, t);
              });
            }

            const processArray = async (array) => {

              let tempArray = [];
              for (let i = 0; i < array.length; i++) {
                if (i % 500 === 0) {
                  await delay(15 * 1000);
                }
                tempArray.push(array[i])
                span.textContent = tempArray.length.toFixed();
                await updateLogByCode(array[i]);
              }

              tempArray.push(span.textContent);
              container.innerHTML = tempArray.length.toFixed();
            }

            const updateLogByCode = async (log: any) => {
              const logRef = firebase.firestore().collection('logs');
              const query = await logRef
                .where('account_key', '==', log.account_key)
                .where('zone_id', '==', this.selectedZone.zone_id)
                .where('code', '==', log.code)
                .where('status', '==', 'incomplete')
                .where('time_out', '==', null)
                .where('timestamp', '>=', this.getStartDate())
                .where('timestamp', '<=', this.getEndDate())
                .limit(1)
                .get();

              if (query.empty) {
                // update exit once matched
                //await updateLogExitByCode(log);
              } else {
                query.docs.map((item) => {
                  // update logs entry once matched
                  let data = {
                    time_out: log.time_out,
                    status: log.status,
                    updatedAt: log.updatedAt,
                    station_id_exit: log.station_id,
                    station_name_exit: log.station_name,
                    device_model_exit: log.device_model_exit,
                    device_uuid_exit: log.device_uuid_exit,
                  }
                  item.ref.update(data).then(() => {
                    // update exit once matched
                    updateLogExitByCode(log);
                  })
                });
              }

            }

            const updateLogExitByCode = async (log: any) => {
              const logRef = firebase.firestore().collection('exit_logs');
              const query = await logRef
                .where('account_key', '==', log.account_key)
                .where('code', '==', log.code)
                .where('matched', '==', false)
                .where('updatedAt', '>=', this.getStartDate())
                .where('updatedAt', '<=', this.getEndDate())
                .orderBy('updatedAt', 'desc')
                .limit(1)
                .get();

              if (query.empty) {
                // No matching exit log found
              } else {
                query.docs.map((item) => {
                  item.ref.update({ matched: true });
                });
              }

            }

            subscribeExit();

          }
        });

      } else if (
        /* Read more about handling dismissals below */
        result.dismiss === Swal.DismissReason.cancel
      ) {
        await Swal.fire(
          'Cancelled',
          'You cancel the checking.',
          'error'
        )
      }
    });
  }

  /**
   * Get maximum allowed start date (today)
   */
  getMaxStartDate(): Date {
    return this.maxDate;
  }

  /**
   * Get minimum allowed end date (start date)
   * Admin users can navigate freely without start date constraint
   */
  getMinEndDate(): Date {
    // Admin users can select any date from minDate onwards
    if (this.isAdmin) {
      return this.minDate;
    }

    return this.startDate || this.minDate;
  }

  /**
   * Get maximum allowed end date (31 days from start date or today, whichever is earlier)
   * Admin users have no 31-day restriction
   */
  getMaxEndDate(): Date {
    if (!this.startDate) return this.maxDate;

    // Admin users can select any date up to maxDate without 31-day restriction
    if (this.isAdmin) {
      return this.maxDate;
    }

    const startDate = new Date(this.startDate);
    const maxAllowedEndDate = new Date(startDate);
    maxAllowedEndDate.setDate(startDate.getDate() + 31);

    // Return the earlier date between maxAllowedEndDate and today
    return maxAllowedEndDate < this.maxDate ? maxAllowedEndDate : this.maxDate;
  }

  /**
   * Calculate days difference between start and end date (public method)
   */
  getDaysDifference(): number {
    if (!this.startDate || !this.endDate) return 0;

    const startDate = new Date(this.startDate);
    const endDate = new Date(this.endDate);
    return this.calculateDaysDifference(startDate, endDate) + 1; // +1 to include both days
  }

  /**
   * Refresh data with current filters
   */
  refreshData() {
    // Delegate to main date range change handler which handles validation
    this.onDateRangeChange();

    // Auto-close sidebar after applying filters
    setTimeout(() => {
      this.sidebarOpen = false;
    }, 300);
  }

  /**
   * Toggle sidebar visibility
   */
  toggleSidebar(event?: Event) {
    // Prevent event propagation if event exists
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (this.sidebarOpen) {
      // If sidebar is open, apply the slide out animation before closing
      setTimeout(() => {
        this.sidebarOpen = false;
        this.cdr.detectChanges();
      }, 300); // Animation duration
    } else {
      this.sidebarOpen = true;
      this.cdr.detectChanges();
    }
  }

  /**
   * Reset filters to today
   */
  resetFilters() {
    const today = new Date();

    this.isSettingQuickRange = true;

    // Update form controls
    this.dateForm.patchValue({
      startDate: today,
      endDate: today
    });

    // Update component properties
    this.startDate = new Date(today);
    this.endDate = new Date(today);
    this.activeQuickDateRange = 'today';

    setTimeout(() => {
      this.isSettingQuickRange = false;
      this.onDateRangeChange();
      this.cdr.detectChanges();
    }, 100);
  }
}
